import {
  APP_ENVS,
  currentAppEnvironment,
} from '@savgroup-front-common/constants/shared/envs';

import {
  getIsValidPreviewBackendName,
  LOCALHOST_BACKEND_URL,
  LOCALHOST_PREVIEW_HOSTNAME,
  PREVIEW_BACKEND_CHOICE_COOKIE,
  PREVIEW_BACKEND_DEFAULT_COOKIE,
  resolvePreviewBackendUrl,
} from '../preview/previewBackend';

interface APIConfigurationInterface {
  actor: string;
  authorization: string;
  assistant: string;
  base: string;
  apiBase: string;
  baseBlobStorage: string;
  carrier: string;
  catalog: string;
  catalogCDN: string;
  cdn: string;
  claim: string;
  config: string;
  control: string;
  document: string;
  geographical: string;
  ids: string;
  imageCDN: string;
  translationCDN: string;
  import: string;
  marketplace: string;
  notification: string;
  owner: string;
  payment: string;
  repairer: string;
  search: string;
  seller: string;
  sparePart: string;
  uiStylesCDN: string;
  warranty: string;
  workflow: string;
  enterpriseAuchan: string;
  businessIntelligence: string;
  graphQlGateway: string;
  b2c: string;
  identityServer: string;
}

export const PREVIEW_API_PARAM = 'api';
const PREVIEW_BACKEND_COOKIE_ATTRIBUTES = 'Path=/; Secure; SameSite=Lax';

const readPreviewBackendCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);

export function setPreviewBackendChoice(name: string): void {
  document.cookie = `${PREVIEW_BACKEND_CHOICE_COOKIE}=${name}; ${PREVIEW_BACKEND_COOKIE_ATTRIBUTES}`;
}

export function clearPreviewBackendChoice(): void {
  document.cookie = `${PREVIEW_BACKEND_CHOICE_COOKIE}=; Max-Age=0; ${PREVIEW_BACKEND_COOKIE_ATTRIBUTES}`;
}

function getPreviewBackendChoice(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const requestedName = params.get(PREVIEW_API_PARAM);

  if (requestedName === '') {
    clearPreviewBackendChoice();

    return undefined;
  }

  if (getIsValidPreviewBackendName(requestedName)) {
    setPreviewBackendChoice(requestedName);

    return requestedName;
  }

  return readPreviewBackendCookie(PREVIEW_BACKEND_CHOICE_COOKIE);
}

function resolveApiUrl({
  readChoice,
}: {
  readChoice: () => string | undefined;
}): string {
  const DEFAULT_BASE_URL = import.meta.env.VITE_BASE_URL;

  if (currentAppEnvironment !== APP_ENVS.DEV) {
    return DEFAULT_BASE_URL;
  }

  const hostname = window.location.hostname;

  if (hostname.endsWith(LOCALHOST_PREVIEW_HOSTNAME)) {
    return LOCALHOST_BACKEND_URL;
  }

  return resolvePreviewBackendUrl({
    hostname,
    choice: readChoice(),
    previewDefault: readPreviewBackendCookie(PREVIEW_BACKEND_DEFAULT_COOKIE),
    configuredUrl: DEFAULT_BASE_URL,
  });
}

export const getApiUrl = (): string =>
  resolveApiUrl({ readChoice: getPreviewBackendChoice });

export const getDefaultApiUrl = (): string =>
  resolveApiUrl({ readChoice: () => undefined });

// ignores the ?api= param, which would overwrite the choice made in another tab
export const getApiUrlFromCookies = (): string =>
  resolveApiUrl({
    readChoice: () => readPreviewBackendCookie(PREVIEW_BACKEND_CHOICE_COOKIE),
  });

const BASE_URL = getApiUrl();

const PREVIEW_API = {
  actor: `${BASE_URL}/ReverseActorService/`,
  authorization: `${BASE_URL}/ReverseAuthorizationService/`,
  assistant: `${BASE_URL}/ReverseAssistantService/`,
  base: `${BASE_URL}/`,
  apiBase: `https://dev-api.revers.io`,
  baseBlobStorage: `https://reversedevstorage.blob.core.windows.net/`,
  carrier: `${BASE_URL}/ReverseCarrierService/`,
  catalog: `${BASE_URL}/ReverseCatalogService/`,

  claim: `${BASE_URL}/ReverseClaimService/`,
  config: `${BASE_URL}/ReverseSellerService/`,
  control: `${BASE_URL}/ReverseControlService/`,
  document: `${BASE_URL}/ReverseDocumentService/`,
  geographical: `${BASE_URL}/ReverseGeographicalService/`,
  ids: `${BASE_URL}:5000/`,
  imageCDN: `https://reversedevstorage.blob.core.windows.net/publicdocuments/`,
  translationCDN: `https://dev-app.revers.io/`,
  import: `${BASE_URL}/ReverseImportService/`,
  marketplace: `${BASE_URL}/ReverseMarketplaceService/`,
  notification: `${BASE_URL}/ReverseNotificationService/`,
  owner: `${BASE_URL}/ReverseOwnerService/`,
  payment: `${BASE_URL}/ReversePaymentService/`,
  repairer: `${BASE_URL}/ReverseRepairerService/`,
  search: `${BASE_URL}/ReverseSearchService/`,
  seller: `${BASE_URL}/ReverseSellerService/`,
  sparePart: `${BASE_URL}/ReverseSparePartsService/`,
  uiStylesCDN: `https://reversedevstorage.blob.core.windows.net/ui-styles/`,
  warranty: `${BASE_URL}/ReverseWarrantyService/`,
  workflow: `${BASE_URL}/ReverseWorkflowService/`,
  enterpriseAuchan: `${BASE_URL}/ReverseEnterpriseAuchanService/`,
  businessIntelligence: `${BASE_URL}/ReverseBusinessIntelligenceService/`,
  graphQlGateway: `${BASE_URL}/ReverseGraphQLGatewayService/graphql`,
  identityServer: `${BASE_URL}/ReverseIdentityServerService/`,
};

const API: Record<APP_ENVS, APIConfigurationInterface> = {
  TEST: {
    actor: '/ReverseActorService/',
    authorization: '/ReverseAuthorizationService/',
    assistant: '/ReverseAssistantService/',
    base: '/',
    apiBase: '',
    baseBlobStorage: 'https://reversedevstorage.blob.core.windows.net/',
    carrier: '/ReverseCarrierService/',
    catalog: '/ReverseCatalogService/',
    catalogCDN: 'https://reversedevstorage.blob.core.windows.net/catalog/',
    cdn: 'https://reversedevstorage.blob.core.windows.net/',
    claim: '/ReverseClaimService/',
    config: '/ReverseSellerService/',
    control: '/ReverseControlService/',
    document: '/ReverseDocumentService/',
    geographical: '/ReverseGeographicalService/',
    ids: '//localhost:5000/',
    imageCDN:
      'https://reversedevstorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://dev-app.revers.io/`,
    import: '/ReverseImportService/',
    marketplace: '/ReverseMarketplaceService/',
    notification: '/ReverseNotificationService/',
    owner: '/ReverseOwnerService/',
    payment: '/ReversePaymentService/',
    repairer: '/ReverseRepairerService/',
    search: '/ReverseSearchService/',
    seller: '/ReverseSellerService/',
    sparePart: '/ReverseSparePartsService/',
    uiStylesCDN: 'https://reversedevstorage.blob.core.windows.net/ui-styles/',
    warranty: '/ReverseWarrantyService/',
    workflow: '/ReverseWorkflowService/',
    enterpriseAuchan: '/ReverseEnterpriseAuchanService/',
    businessIntelligence: '/ReverseBusinessIntelligenceService/',
    graphQlGateway: '/ReverseGraphQLGatewayService/graphql',
    b2c: 'https://devreversioapp.b2clogin.com/devreversioapp',
    identityServer: '/ReverseIdentityServerService/',
  },
  LOCAL: {
    actor: '//localhost/ReverseActorService/',
    authorization: '//localhost/ReverseAuthorizationService/',
    assistant: '//localhost/ReverseAssistantService/',
    base: '//localhost/',
    apiBase: '//localhost',
    baseBlobStorage: 'https://reversedevstorage.blob.core.windows.net/',
    carrier: '//localhost/ReverseCarrierService/',
    catalog: '//localhost/ReverseCatalogService/',
    catalogCDN: 'https://reversedevstorage.blob.core.windows.net/catalog/',
    cdn: 'https://reversedevstorage.blob.core.windows.net/',
    claim: '//localhost/ReverseClaimService/',
    config: '//localhost/ReverseSellerService/',
    control: '//localhost/ReverseControlService/',
    document: '//localhost/ReverseDocumentService/',
    geographical: '//localhost/ReverseGeographicalService/',
    ids: '//localhost:5000/',
    imageCDN:
      'https://reversedevstorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://dev-app.revers.io/`,
    import: '//localhost/ReverseImportService/',
    marketplace: '//localhost/ReverseMarketplaceService/',
    notification: '//localhost/ReverseNotificationService/',
    owner: '//localhost/ReverseOwnerService/',
    payment: '//localhost/ReversePaymentService/',
    repairer: '//localhost/ReverseRepairerService/',
    search: '//localhost/ReverseSearchService/',
    seller: '//localhost/ReverseSellerService/',
    sparePart: '//localhost/ReverseSparePartsService/',
    uiStylesCDN: 'https://reversedevstorage.blob.core.windows.net/ui-styles/',
    warranty: '//localhost/ReverseWarrantyService/',
    workflow: '//localhost/ReverseWorkflowService/',
    enterpriseAuchan: '//localhost/ReverseEnterpriseAuchanService/',
    businessIntelligence: '//localhost/ReverseBusinessIntelligenceService/',
    graphQlGateway: '//localhost/ReverseGraphQLGatewayService/graphql',
    b2c: 'https://devreversioapp.b2clogin.com/devreversioapp',
    identityServer: '//localhost/ReverseIdentityServerService/',
  },
  DEFAULT: {
    actor: 'https://dev-api.revers.io/ReverseActorService/',
    authorization: 'https://dev-api.revers.io/ReverseAuthorizationService/',
    assistant: 'https://dev-api.revers.io/ReverseAssistantService/',
    base: 'https://dev-api.revers.io/',
    apiBase: 'https://dev-api.revers.io',
    baseBlobStorage: 'https://reversedevstorage.blob.core.windows.net/',
    carrier: 'https://dev-api.revers.io/ReverseCarrierService/',
    catalog: 'https://dev-api.revers.io/ReverseCatalogService/',

    claim: 'https://dev-api.revers.io/ReverseClaimService/',
    config: 'https://dev-api.revers.io/ReverseSellerService/',
    control: 'https://dev-api.revers.io/ReverseControlService/',
    document: 'https://dev-api.revers.io/ReverseDocumentService/',
    geographical: 'https://dev-api.revers.io/ReverseGeographicalService/',
    ids: 'https://dev-api.revers.io:5000/',
    imageCDN:
      'https://reversedevstorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://dev-app.revers.io/`,
    import: 'https://dev-api.revers.io/ReverseImportService/',
    marketplace: 'https://dev-api.revers.io/ReverseMarketplaceService/',
    notification: 'https://dev-api.revers.io/ReverseNotificationService/',
    owner: 'https://dev-api.revers.io/ReverseOwnerService/',
    payment: 'https://dev-api.revers.io/ReversePaymentService/',
    repairer: 'https://dev-api.revers.io/ReverseRepairerService/',
    search: 'https://dev-api.revers.io/ReverseSearchService/',
    seller: 'https://dev-api.revers.io/ReverseSellerService/',
    sparePart: 'https://dev-api.revers.io/ReverseSparePartsService/',
    uiStylesCDN: 'https://reversedevstorage.blob.core.windows.net/ui-styles/',
    warranty: 'https://dev-api.revers.io/ReverseWarrantyService/',
    workflow: 'https://dev-api.revers.io/ReverseWorkflowService/',
    enterpriseAuchan:
      'https://dev-api.revers.io/ReverseEnterpriseAuchanService/',
    businessIntelligence:
      'https://dev-api.revers.io/ReverseBusinessIntelligenceService/',
    graphQlGateway:
      'https://dev-api.revers.io/ReverseGraphQLGatewayService/graphql',

    b2c: 'https://devreversioapp.b2clogin.com/devreversioapp',
    catalogCDN: 'https://reversedevstorage.blob.core.windows.net/catalog/',
    cdn: 'https://reversedevstorage.blob.core.windows.net/',
    identityServer: 'https://dev-api.revers.io/ReverseIdentityServerService/',
  },
  DEV: {
    actor: 'https://dev-api.revers.io/ReverseActorService/',
    authorization: 'https://dev-api.revers.io/ReverseAuthorizationService/',
    assistant: 'https://dev-api.revers.io/ReverseAssistantService/',
    base: 'https://dev-api.revers.io/',
    apiBase: 'https://dev-api.revers.io',
    baseBlobStorage: 'https://reversedevstorage.blob.core.windows.net/',
    carrier: 'https://dev-api.revers.io/ReverseCarrierService/',
    catalog: 'https://dev-api.revers.io/ReverseCatalogService/',
    catalogCDN: 'https://reversedevstorage.blob.core.windows.net/catalog/',
    cdn: 'https://reversedevstorage.blob.core.windows.net/',
    claim: 'https://dev-api.revers.io/ReverseClaimService/',
    config: 'https://dev-api.revers.io/ReverseSellerService/',
    control: 'https://dev-api.revers.io/ReverseControlService/',
    document: 'https://dev-api.revers.io/ReverseDocumentService/',
    geographical: 'https://dev-api.revers.io/ReverseGeographicalService/',
    ids: 'https://dev-api.revers.io:5000/',
    imageCDN:
      'https://reversedevstorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://dev-app.revers.io/`,
    import: 'https://dev-api.revers.io/ReverseImportService/',
    marketplace: 'https://dev-api.revers.io/ReverseMarketplaceService/',
    notification: 'https://dev-api.revers.io/ReverseNotificationService/',
    owner: 'https://dev-api.revers.io/ReverseOwnerService/',
    payment: 'https://dev-api.revers.io/ReversePaymentService/',
    repairer: 'https://dev-api.revers.io/ReverseRepairerService/',
    search: 'https://dev-api.revers.io/ReverseSearchService/',
    seller: 'https://dev-api.revers.io/ReverseSellerService/',
    sparePart: 'https://dev-api.revers.io/ReverseSparePartsService/',
    uiStylesCDN: 'https://reversedevstorage.blob.core.windows.net/ui-styles/',
    warranty: 'https://dev-api.revers.io/ReverseWarrantyService/',
    workflow: 'https://dev-api.revers.io/ReverseWorkflowService/',
    enterpriseAuchan:
      'https://dev-api.revers.io/ReverseEnterpriseAuchanService/',
    businessIntelligence:
      'https://dev-api.revers.io/ReverseBusinessIntelligenceService/',
    graphQlGateway:
      'https://dev-api.revers.io/ReverseGraphQLGatewayService/graphql',
    b2c: 'https://devreversioapp.b2clogin.com/devreversioapp',
    identityServer: 'https://dev-api.revers.io/ReverseIdentityServerService/',
  },
  DEMO: {
    actor: 'https://demo-api.revers.io/ReverseActorService/',
    authorization: 'https://demo-api.revers.io/ReverseAuthorizationService/',
    assistant: 'https://demo-api.revers.io/ReverseAssistantService/',
    base: 'https://demo-api.revers.io/',
    apiBase: 'https://demo-api.revers.io',
    baseBlobStorage: 'https://demoreversestorage.blob.core.windows.net/',
    carrier: 'https://demo-api.revers.io/ReverseCarrierService/',
    catalog: 'https://demo-api.revers.io/ReverseCatalogService/',
    catalogCDN: 'https://demoreversestorage.blob.core.windows.net/catalog/',
    cdn: 'https://demoreversestorage.blob.core.windows.net/',
    claim: 'https://demo-api.revers.io/ReverseClaimService/',
    config: 'https://demo-api.revers.io/ReverseSellerService/',
    control: 'https://demo-api.revers.io/ReverseControlService/',
    document: 'https://demo-api.revers.io/ReverseDocumentService/',
    geographical: 'https://demo-api.revers.io/ReverseGeographicalService/',
    ids: 'https://demo-api.revers.io:5000/',
    imageCDN:
      'https://demoreversestorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://demo-app.revers.io/`,
    import: 'https://demo-api.revers.io/ReverseImportService/',
    marketplace: 'https://demo-api.revers.io/ReverseMarketplaceService/',
    notification: 'https://demo-api.revers.io/ReverseNotificationService/',
    owner: 'https://demo-api.revers.io/ReverseOwnerService/',
    payment: 'https://demo-api.revers.io/ReversePaymentService/',
    repairer: 'https://demo-api.revers.io/ReverseRepairerService/',
    search: 'https://demo-api.revers.io/ReverseSearchService/',
    seller: 'https://demo-api.revers.io/ReverseSellerService/',
    sparePart: 'https://demo-api.revers.io/ReverseSparePartsService/',
    uiStylesCDN: 'https://demoreversestorage.blob.core.windows.net/ui-styles/',
    warranty: 'https://demo-api.revers.io/ReverseWarrantyService/',
    workflow: 'https://demo-api.revers.io/ReverseWorkflowService/',
    enterpriseAuchan:
      'https://demo-api.revers.io/ReverseEnterpriseAuchanService/',
    businessIntelligence:
      'https://demo-api.revers.io/ReverseBusinessIntelligenceService/',
    graphQlGateway:
      'https://demo-api.revers.io/ReverseGraphQLGatewayService/graphql',
    b2c: 'https://demoreversioapp.b2clogin.com/demoreversioapp',
    identityServer: 'https://demo-api.revers.io/ReverseIdentityServerService/',
  },
  PROD: {
    actor: 'https://api.revers.io/ReverseActorService/',
    authorization: 'https://api.revers.io/ReverseAuthorizationService/',
    assistant: 'https://api.revers.io/ReverseAssistantService/',
    base: 'https://api.revers.io/',
    apiBase: 'https://api.revers.io',
    baseBlobStorage: 'https://prodreversestorage.blob.core.windows.net/',
    carrier: 'https://api.revers.io/ReverseCarrierService/',
    catalog: 'https://api.revers.io/ReverseCatalogService/',
    catalogCDN: 'https://prodreversestorage.blob.core.windows.net/catalog/',
    cdn: 'https://prodreversestorage.blob.core.windows.net/',
    claim: 'https://api.revers.io/ReverseClaimService/',
    config: 'https://api.revers.io/ReverseSellerService/',
    control: 'https://api.revers.io/ReverseControlService/',
    document: 'https://api.revers.io/ReverseDocumentService/',
    geographical: 'https://api.revers.io/ReverseGeographicalService/',
    ids: 'https://api.revers.io:5000/',
    imageCDN:
      'https://prodreversestorage.blob.core.windows.net/publicdocuments/',
    translationCDN: `https://app.revers.io/`,
    import: 'https://api.revers.io/ReverseImportService/',
    marketplace: 'https://api.revers.io/ReverseMarketplaceService/',
    notification: 'https://api.revers.io/ReverseNotificationService/',
    owner: 'https://api.revers.io/ReverseOwnerService/',
    payment: 'https://api.revers.io/ReversePaymentService/',
    repairer: 'https://api.revers.io/ReverseRepairerService/',
    search: 'https://api.revers.io/ReverseSearchService/',
    seller: 'https://api.revers.io/ReverseSellerService/',
    sparePart: 'https://api.revers.io/ReverseSparePartsService/',
    uiStylesCDN: 'https://prodreversestorage.blob.core.windows.net/ui-styles/',
    warranty: 'https://api.revers.io/ReverseWarrantyService/',
    workflow: 'https://api.revers.io/ReverseWorkflowService/',
    enterpriseAuchan: 'https://api.revers.io/ReverseEnterpriseAuchanService/',
    businessIntelligence:
      'https://api.revers.io/ReverseBusinessIntelligenceService/',
    graphQlGateway:
      'https://api.revers.io/ReverseGraphQLGatewayService/graphql',
    b2c: 'https://reversioapp.b2clogin.com/reversioapp',
    identityServer: 'https://api.revers.io/ReverseIdentityServerService/',
  },
};

const getCurrentApiConfigs = (): APIConfigurationInterface => {
  switch (currentAppEnvironment) {
    case APP_ENVS.DEMO: {
      if (BASE_URL) {
        return {
          ...API.DEMO,
          ...PREVIEW_API,
        };
      }

      return API.DEMO;
    }
    case APP_ENVS.DEV: {
      if (BASE_URL) {
        return {
          ...API.DEV,
          ...PREVIEW_API,
        };
      }

      return API.DEV;
    }
    case APP_ENVS.LOCAL: {
      if (BASE_URL) {
        return {
          ...API.LOCAL,
          ...PREVIEW_API,
        };
      }

      return API.LOCAL;
    }
    case APP_ENVS.PROD: {
      if (BASE_URL) {
        return {
          ...API.PROD,
          ...PREVIEW_API,
        };
      }

      return API.PROD;
    }
    case APP_ENVS.DEFAULT:
    default: {
      if (BASE_URL) {
        return {
          ...API.DEV,
          ...PREVIEW_API,
        };
      }

      return API.DEV;
    }
  }
};

export default getCurrentApiConfigs();
